import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';
import { cn } from '@/lib/utils';

/**
 * Etiqueta do sistema. O padrão é neutro — o amarelo da marca não vira etiqueta por acidente
 * (§17). As variantes de cor são as do tema, por token (ADR-043): **sólidas** para o que precisa
 * saltar aos olhos (prejuízo, acima da meta) e **tingidas** (`*-soft`) para situação (ativo,
 * pendente, falhou — o `StatusBadge` as usa com ícone). `dashed` marca o provisório.
 */
const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-xs font-medium [&_svg]:size-3',
  {
    variants: {
      variant: {
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'text-foreground',
        destructive: 'border-destructive bg-destructive text-destructive-foreground',
        warning: 'border-warning bg-warning text-warning-foreground',
        success: 'border-success bg-success text-success-foreground',
        excellent: 'border-excellent bg-excellent text-excellent-foreground',
        'destructive-soft':
          'border-destructive/25 bg-destructive/8 text-danger-ink dark:bg-destructive/15',
        'warning-soft': 'border-warning/30 bg-warning/8 text-warning-ink dark:bg-warning/15',
        'success-soft': 'border-success/25 bg-success/8 text-success-ink dark:bg-success/15',
        'excellent-soft':
          'border-excellent/30 bg-excellent/8 text-excellent-ink dark:bg-excellent/15',
        'info-soft': 'border-info/25 bg-info/8 text-info-ink dark:bg-info/15',
        'brand-soft': 'border-brand/50 bg-brand-soft text-foreground',
        neutral: 'border-border bg-secondary text-muted-foreground',
      },
      dashed: { true: 'border-dashed', false: '' },
    },
    defaultVariants: { variant: 'secondary', dashed: false },
  },
);

export type BadgeProps = ComponentProps<'span'> & VariantProps<typeof badgeVariants>;

export function Badge({ className, variant, dashed, ...props }: BadgeProps) {
  return (
    <span
      data-slot="badge"
      className={cn(badgeVariants({ variant, dashed }), className)}
      {...props}
    />
  );
}
